import { useDispatch, useSelector } from "react-redux";

import { LookupIcon, ProfilingIcon } from "@/assets/icons";
import {
  buildGeohashOptions,
  buildH3Options
} from "@/components/spatialAnalysis/spatialOutputAnalysis/utils";
import TabSelect from "@/components/tabSelect";
import Dropdown from "@/components/ui/dropdown";
import type { IOptionItem } from "@/components/ui/dropdown/types";
import { useValidationAnalysisConfigInput } from "@/hooks/spatialAnalysis/useValidationAnalysisConfig";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import { setSpatialConfig } from "@/store/slice/spatialSlice";
import {
  EOutputAnalysisType,
  EOutputGridType,
  OutputGridType
} from "@/types/spatial";

import { twMerge } from "tailwind-merge";

import { H3CustomOption } from "./data";
import GridOutput from "./gridOutput";
import ProfilingOutput from "./profilingOutput";

const OUTPUT_VISUALIZATION = [
  {
    value: EOutputAnalysisType.GRID,
    label: "Grid",
    icon: <LookupIcon />
  },
  {
    value: EOutputAnalysisType.PROFILING,
    label: "Profiling",
    icon: <ProfilingIcon />
  }
];

export const renderH3Option = (option: H3CustomOption) => {
  return (
    <div className="flex justify-between">
      <span
        className={cn(
          "text-xs text-txtHeading w-1/4",
          option.disabled && "text-gray-300"
        )}
      >
        {option.value}
      </span>
      <span
        className={cn(
          "text-xs text-txtHeading w-1/4",
          option.disabled && "text-gray-300"
        )}
      >
        {option.radius}
      </span>
      <span
        className={cn(
          "text-xs text-txtHeading text-right w-2/4",
          option.disabled && "text-gray-300"
        )}
      >
        {option.label}
      </span>
    </div>
  );
};

const SpatialOutputAnalysis = ({
  isOutputValidationError = false,
  isResolutionValidationError = false
}: {
  isOutputValidationError?: boolean;
  isResolutionValidationError?: boolean;
}) => {
  const dispatch = useDispatch();
  const { spatialConfig } = useSelector((state: RootState) => state.spatial);
  const { validationErrors } = useValidationAnalysisConfigInput();

  const handleChangeGridType = (value: OutputGridType) =>
    dispatch(setSpatialConfig({ ...spatialConfig, gridType: value }));

  const handleChangeGridResolution = (value: IOptionItem | null) =>
    dispatch(
      setSpatialConfig({
        ...spatialConfig,
        gridResolution: value
      })
    );

  const handleChangeH3Resolution = (value: H3CustomOption | null) =>
    dispatch(
      setSpatialConfig({
        ...spatialConfig,
        h3Resolution: value
      })
    );

  const renderGeohashOption = (option: IOptionItem) => (
    <span
      className={cn(
        "text-xs text-txtHeading",
        option.disabled && "text-gray-300"
      )}
    >
      {option.label}
    </span>
  );

  return (
    <div className="flex flex-col px-4 gap-1 mt-4">
      <span className="font-sans font-semibold text-txtHeading text-sm">
        Output Analysis
      </span>
      <p className="text-txtBody font-sans text-[10px]">
        Select your preferred output visualization.
      </p>
      <div
        className={twMerge(
          isOutputValidationError &&
            "border border-cinnabar rounded-xl px-2 pt-1"
        )}
      >
        <div className="flex h-[38px] bg-whisper rounded-md items-center px-1 mt-1">
          <TabSelect
            tabList={OUTPUT_VISUALIZATION}
            selected={spatialConfig?.outputType}
            className="bg-transparent w-full mt-0 px-0"
            buttonClassName="flex"
            buttonSelectedClassName="!bg-white !border-teal-500 font-bold"
            wrapperListClassName="w-full justify-between gap-1"
            onSelect={(value) => {
              dispatch(
                setSpatialConfig({
                  ...spatialConfig,
                  outputType: value as EOutputAnalysisType
                })
              );
            }}
          />
        </div>
        <div className="flex flex-col my-2 gap-3">
          {spatialConfig?.outputType === EOutputAnalysisType.GRID && (
            <GridOutput
              selected={spatialConfig.gridType}
              onChange={handleChangeGridType}
            />
          )}
          {spatialConfig?.outputType === EOutputAnalysisType.PROFILING && (
            <ProfilingOutput />
          )}
        </div>
      </div>
      {isOutputValidationError && (
        <p className="px-4 text-[10px] text-cinnabar self-start my-1">
          {validationErrors.outputAnalysisMessage}
        </p>
      )}
      {/* Resolution settings */}
      <div
        className={twMerge(
          "flex-col",
          spatialConfig.outputType === EOutputAnalysisType.GRID
            ? "flex"
            : "hidden"
        )}
      >
        <span className="text-txtHeading font-bold font-sans text-sm">
          Resolution Settings
        </span>
        {spatialConfig?.gridType === EOutputGridType.GEOHASH && (
          <Dropdown
            isValidationError={isResolutionValidationError}
            className="mt-3"
            placeholder="Resolution"
            value={spatialConfig.gridResolution}
            data={buildGeohashOptions(spatialConfig.areaSize)}
            renderOption={renderGeohashOption}
            onSelect={handleChangeGridResolution}
          />
        )}
        {spatialConfig?.gridType === EOutputGridType.H3 && (
          <Dropdown
            isValidationError={isResolutionValidationError}
            className="mt-3"
            placeholder="Resolution"
            value={spatialConfig.h3Resolution}
            data={buildH3Options(spatialConfig.areaSize)}
            onSelect={handleChangeH3Resolution}
            renderOption={renderH3Option}
            renderSelectValue={renderH3Option}
          />
        )}
        {isResolutionValidationError && (
          <p className="px-2 text-[10px] text-cinnabar self-start mt-1">
            {validationErrors.resolutionMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export default SpatialOutputAnalysis;
